import { useState } from 'react';
import {
  Box,
  Tooltip,
  Typography,
  Stack,
  styled,
  TextField,
  Switch,
  FormGroup,
  FormControlLabel,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Alert,
} from '@mui/material';
import { filterTree } from './trees/treeUtils';
import StatusReportsTree from './trees/StatusReportsTree';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import LoadingIndicator from '../../../components/Global/loading-indicator/loading-indicator';
import { GetStatusReport } from '../../../api/MyIMO';
import HeaderStack from './HeaderStack';

const StyledIMOInputBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '.5rem',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const StatusReports = ({ producerID }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const initialExpand = searchParams.get('expand') === 'true';
  const initialType = searchParams.get('type') || 0;

  const [type, setType] = useState(initialType);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [expandAll, setExpandAll] = useState(initialExpand);

  const updateParams = (newParams) => {
    const updatedParams = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams);
  };

  const getStatusReportResult = useQuery({
    queryKey: ['getStatusReport', producerID, type],
    queryFn: () =>
      GetStatusReport([
        { queryParam: 'producerID', queryVal: producerID },
        { queryParam: 'reportType', queryVal: type },
      ])(),
    enabled: !!producerID,
    placeholderData: (prevData) => prevData,
  });

  const handleSearchChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    updateParams({ search: value });
  };

  const handleExpandAll = () => {
    setExpandAll((prev) => {
      const updated = !prev;
      updateParams({ expand: updated });
      return updated;
    });
  };

  const handleType = (e) => {
    setSearchTerm('');
    updateParams({ type: e.target.value });
    setType(e.target.value);
  };

  const filteredData =
    searchTerm && getStatusReportResult.data?.value
      ? filterTree(getStatusReportResult.data?.value, searchTerm) || {
          ...getStatusReportResult.data?.value,
          children: [],
        }
      : getStatusReportResult.data?.value;

  // loading states
  const showInitialLoad = getStatusReportResult.isPending;
  const showTreeSpinner = !getStatusReportResult.isPending && getStatusReportResult.isRefetching;
  const showData =
    getStatusReportResult.isSuccess &&
    getStatusReportResult.data.value &&
    !showInitialLoad &&
    getStatusReportResult.data.value.name !== null;

  return (
    <Stack spacing={4}>
      {getStatusReportResult.isError && (
        <Box padding={'1rem 0'}>
          <Alert sx={{ width: 'fit-content' }} severity="warning">
            Something went wrong
          </Alert>
        </Box>
      )}
      {getStatusReportResult.isSuccess &&
        (!getStatusReportResult.data?.value || !getStatusReportResult.data?.value.children) && (
          <Box padding={'1rem 0'}>
            <Alert sx={{ width: 'fit-content' }} severity="info">
              No data to display
            </Alert>
          </Box>
        )}
      {showInitialLoad && <LoadingIndicator />}
      {showData && (
        <>
          <Stack spacing={2}>
            <Typography sx={{ padding: '1rem 0', textTransform: 'capitalize' }} variant="h6">
              {(getStatusReportResult.data?.value?.name || '').replace(
                /^([^:]+):(.+)$/,
                (_, before, after) => before.toUpperCase() + ':' + after.toLowerCase()
              )}
            </Typography>
            <HeaderStack
              cards={[
                {
                  label: 'Type',
                  field: getStatusReportResult.data.value.prodType,
                  currency: false,
                },
                {
                  label: 'Contract Status',
                  field: getStatusReportResult.data.value.contractStatus,
                  currency: false,
                },
              ]}
            />
          </Stack>
          <StyledIMOInputBox>
            <Box
              sx={{
                display: 'flex',
                alignItems: {
                  xs: 'flex-end',
                  sm: 'center',
                },
                gap: '1rem',
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                justifyContent: 'flex-start',
                flexWrap: 'wrap',
              }}
            >
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                sx={{
                  width: {
                    xs: '100%',
                    sm: '15rem',
                  },
                }}
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <FormControl
                size="small"
                fullwidth="true"
                sx={{
                  width: {
                    xs: '100%',
                    sm: 165,
                  },
                }}
              >
                <InputLabel id="status-reports-select-label">Type</InputLabel>
                <Select
                  labelId="status-reports-select-label"
                  value={type}
                  label="Type"
                  onChange={handleType}
                >
                  <MenuItem value={0}>
                    <em>Show all</em>
                  </MenuItem>
                  <MenuItem value={1}>Active Only</MenuItem>
                  <MenuItem value={2}>Terminated only</MenuItem>
                  <MenuItem value={3}>SUB-IMO only</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Search by producer name or contract, or filter by user type and contract status">
                <InfoOutlinedIcon color={'#f3f3f3'} />
              </Tooltip>
            </Box>
            <FormGroup
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
                alignItems: {
                  xs: 'flex-end',
                },
              }}
            >
              <FormControlLabel
                labelPlacement="start"
                control={<Switch checked={expandAll} onChange={handleExpandAll} />}
                label="Expand all"
              />
            </FormGroup>
          </StyledIMOInputBox>
          {showTreeSpinner ? (
            <LoadingIndicator />
          ) : (
            <StatusReportsTree data={filteredData} expandAll={expandAll} />
          )}
        </>
      )}
    </Stack>
  );
};

export default StatusReports;
